<template>
  <div class="pos-screen">
    <!-- Cabecera: sucursal + estado de caja -->
    <div class="iq-card pos-header mb-3">
      <div class="card-body d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div class="d-flex align-items-center gap-3 flex-wrap">
          <h4 class="mb-0 text-white"><i class="fa-solid fa-cash-register me-2" aria-hidden="true"></i>{{ $t('pos.lbl_pos') }}</h4>
          <!-- Preview del proximo numero de factura (informativo). -->
          <span class="pos-invoice-chip" v-if="store.catalog.next_invoice_number">
            <i class="fa-solid fa-file-invoice me-1" aria-hidden="true"></i>{{ $t('pos.lbl_next_invoice') }} {{ store.catalog.next_invoice_number }}
          </span>
          <!-- Catalogo servido desde la cache local (offline). -->
          <span class="pos-invoice-chip" v-if="offlineCatalog">
            <i class="fa-solid fa-clock-rotate-left me-1" aria-hidden="true"></i>{{ $t('pos.lbl_cached_catalog') }}
          </span>
        </div>

        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select id="pos-branch" class="form-select form-select-sm pos-branch-select" :value="store.branchId" @change="changeBranch($event.target.value)" :aria-label="$t('pos.lbl_branch')">
            <option :value="null">{{ $t('pos.lbl_select_branch') }}</option>
            <option v-for="branch in store.branches" :key="`branch-${branch.id}`" :value="branch.id">{{ branch.name }}</option>
          </select>

          <!-- Ordenes en espera (park/hold). -->
          <button type="button" class="btn btn-sm btn-light pos-tap" v-if="store.hasLines" @click="holdCurrentOrder">
            <i class="fa-solid fa-circle-pause me-1" aria-hidden="true"></i>{{ $t('pos.lbl_hold') }}
          </button>
          <button type="button" class="btn btn-sm btn-light pos-tap" v-if="holdsCount > 0" @click="showHolds = true">
            <i class="fa-solid fa-bars-staggered me-1" aria-hidden="true"></i>{{ $t('pos.lbl_resume') }} ({{ holdsCount }})
          </button>

          <!-- Cola de ventas offline pendientes de sincronizar (Fase 2). -->
          <button type="button" class="btn btn-sm btn-warning pos-tap" v-if="queueCount > 0" :disabled="syncing || !isOnline" @click="syncQueue">
            <span v-if="syncing" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            <i v-else class="fa-solid fa-cloud-arrow-up me-1" aria-hidden="true"></i>{{ $t('pos.lbl_sync') }} ({{ queueCount }})
          </button>

          <!-- Conflictos de sincronizacion (ventas que el backend rechazo). -->
          <button type="button" class="btn btn-sm btn-danger pos-tap" v-if="conflictList.length > 0" @click="showConflicts = true">
            <i class="fa-solid fa-triangle-exclamation me-1" aria-hidden="true"></i>{{ $t('pos.lbl_conflicts') }} ({{ conflictList.length }})
          </button>

          <!-- Orden de trabajo: cobrar la cita en curso sin re-teclearla. -->
          <OpenOrdersModal />

          <CashPanel @refresh="loadCatalog" />

          <!-- Reporte X del dia (resumen de mostrador, read-only). -->
          <DailyReportPanel />

          <!-- Recargar catalogo (con texto para que se entienda). -->
          <button type="button" class="btn btn-sm btn-light pos-tap" :disabled="!store.branchId || store.loadingCatalog" @click="loadCatalog">
            <i class="fa-solid fa-arrows-rotate me-1" :class="{ 'fa-spin': store.loadingCatalog }" aria-hidden="true"></i>{{ $t('pos.lbl_reload') }}
          </button>

          <!-- Estado de conexion: verde en linea / rojo sin conexion (al final, como status). -->
          <span
            class="pos-signal"
            :class="isOnline ? 'pos-signal-on' : 'pos-signal-off'"
            role="status"
            :title="isOnline ? $t('pos.lbl_online') : $t('pos.lbl_offline')"
            :aria-label="isOnline ? $t('pos.lbl_online') : $t('pos.lbl_offline')"
          >
            <i class="fa-solid" :class="isOnline ? 'fa-wifi' : 'fa-plane-up'" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- Sesion / permisos: el backend exige auth:sanctum + permission:*_booking -->
    <AlertMessage v-if="authError" variant="warning" :message="authError === 403 ? $t('pos.lbl_forbidden') : $t('pos.lbl_unauthenticated')" />

    <!-- Error global (p.ej. catalogo) -->
    <AlertMessage v-else-if="globalError" :message="globalError" :title="$t('pos.lbl_error')" />

    <!-- Resultado de la venta: sustituye al POS hasta que se inicie otra -->
    <SaleResult v-if="store.sale" :sale="store.sale" @new-sale="newSale" />

    <template v-else-if="!authError">
      <AlertMessage variant="warning" v-if="store.branchId && !store.loadingCatalog && !store.catalog.cash_register" :message="$t('pos.lbl_cash_register_warning')" />

      <div class="row g-3">
        <div class="col-lg-7">
          <CatalogPanel />
        </div>
        <div class="col-lg-5">
          <div class="pos-checkout d-flex flex-column gap-3">
            <div class="alert alert-success py-2 mb-0" role="status" v-if="avisoEnvio">
              <i class="fa-solid fa-paper-plane me-1" aria-hidden="true"></i>{{ avisoEnvio }}
            </div>
            <CustomerPanel @auth-error="onAuthError" />
            <CartPanel />
            <DeliveryPanel />
            <PaymentPanel @charge="charge" @send="sendToCashier" />
          </div>
        </div>
      </div>
    </template>

    <!-- Órdenes en espera: retomar una. -->
    <PosModal v-model="showHolds" :title="$t('pos.lbl_held_orders')" :done-label="$t('pos.lbl_done')" :close-label="$t('pos.lbl_cancel')">
      <p class="text-muted small mb-2" v-if="!holdsList.length">{{ $t('pos.lbl_no_holds') }}</p>
      <div v-for="hold in holdsList" :key="`hold-${hold.id}`" class="pos-line">
        <div class="d-flex justify-content-between align-items-center gap-2">
          <div>
            <strong class="d-block">{{ hold.label }}</strong>
            <small class="text-muted">{{ (hold.snapshot.services.length + hold.snapshot.products.length) }} ítems</small>
          </div>
          <div class="d-flex gap-1">
            <button type="button" class="btn btn-sm btn-primary pos-tap" @click="retrieveHold(hold)">{{ $t('pos.lbl_resume') }}</button>
            <button type="button" class="btn btn-sm btn-outline-danger pos-tap" @click="() => { removeHold(hold.id); refreshHolds() }" :aria-label="$t('pos.lbl_discard')">
              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </PosModal>

    <!-- Panel de conflictos de sincronizacion (Fase 2). -->
    <PosModal v-model="showConflicts" :title="$t('pos.lbl_conflicts')" :done-label="$t('pos.lbl_done')" :close-label="$t('pos.lbl_cancel')">
      <p class="text-muted small mb-2">{{ $t('pos.lbl_conflicts_help') }}</p>
      <div v-for="item in conflictList" :key="`cf-${item.id}`" class="pos-line">
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div>
            <strong class="text-danger d-block">{{ item.conflict }}</strong>
            <small class="text-muted">{{ (item.payload && item.payload.payment_method) || '' }}</small>
          </div>
          <div class="d-flex gap-1">
            <button type="button" class="btn btn-sm btn-outline-primary pos-tap" @click="retryConflict(item.id)">{{ $t('pos.lbl_retry') }}</button>
            <button type="button" class="btn btn-sm btn-outline-danger pos-tap" @click="discardConflict(item.id)">{{ $t('pos.lbl_discard') }}</button>
          </div>
        </div>
      </div>
    </PosModal>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './components/AlertMessage.vue'
import CashPanel from './components/CashPanel.vue'
import OpenOrdersModal from './components/OpenOrdersModal.vue'
import DailyReportPanel from './components/DailyReportPanel.vue'
import CatalogPanel from './components/CatalogPanel.vue'
import CustomerPanel from './components/CustomerPanel.vue'
import CartPanel from './components/CartPanel.vue'
import DeliveryPanel from './components/DeliveryPanel.vue'
import PaymentPanel from './components/PaymentPanel.vue'
import SaleResult from './components/SaleResult.vue'
import { usePos } from '../store/pos'
import { posRequest, flattenErrors } from './usePosRequest'
import { POS_BRANCH_LIST, POS_CATALOG, POS_SALE, POS_SEND_ORDER } from './constants/pos'
import { saveCatalog, loadCatalog as loadCachedCatalog } from './offlineCatalog'
import { enqueueSale, queueSize, peekQueue, removeFromQueue, conflicts, setConflict } from './offlineQueue'
import { holdCount, peekHolds, addHold, removeHold } from './posHolds'
import PosModal from './components/PosModal.vue'

const store = usePos()
const { t } = useI18n()

const authError = ref(null)
const globalError = ref('')

// --- Estado de conexión (Fase 1 offline) ---
const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
const offlineCatalog = ref(false) // el catálogo pintado viene de la caché local

// --- Cola de ventas offline (Fase 2) ---
const queueCount = ref(queueSize())
const syncing = ref(false)
const conflictList = ref(conflicts())
const showConflicts = ref(false)

const refreshQueue = () => {
  queueCount.value = queueSize()
  conflictList.value = conflicts()
}

const discardConflict = (id) => {
  removeFromQueue(id)
  refreshQueue()
}

// --- Órdenes en espera (park/hold) ---
const holdsCount = ref(holdCount())
const showHolds = ref(false)
const holdsList = ref(peekHolds())

const refreshHolds = () => {
  holdsCount.value = holdCount()
  holdsList.value = peekHolds()
}

/** Aparca el carrito actual y limpia para atender otra orden. */
const holdCurrentOrder = () => {
  if (!store.hasLines) return
  const label = store.customer?.name || `${store.services.length + store.products.length} items`
  addHold(label, store.snapshotCart())
  store.resetSale()
  refreshHolds()
}

/** Retoma una orden en espera (reemplaza el carrito actual). */
const retrieveHold = (hold) => {
  store.restoreCart(hold.snapshot)
  removeHold(hold.id)
  refreshHolds()
  showHolds.value = false
}

const retryConflict = (id) => {
  setConflict(id, null) // limpia el conflicto -> el proximo sync lo reintenta
  refreshQueue()
  syncQueue()
}

const setOnline = () => {
  isOnline.value = true
  // Al recuperar la red, se intenta sincronizar la cola.
  syncQueue()
}
const setOffline = () => { isOnline.value = false }

/**
 * Reenvía las ventas encoladas a `pos/sale`. El backend deduplica por
 * idempotency_key: si ya estaba registrada, la devuelve (already_synced) y se
 * quita de la cola. Un fallo de negocio (p.ej. stock) la deja en cola para
 * revisar. Un fallo de red detiene el ciclo (se reintenta luego).
 */
const syncQueue = async () => {
  if (syncing.value || !isOnline.value) return

  syncing.value = true

  for (const item of peekQueue()) {
    if (item.conflict) continue // los conflictos no se reintentan solos

    const res = await posRequest(POS_SALE(), item.payload)

    if (res.ok) {
      removeFromQueue(item.id)
    } else if (res.httpStatus === 0) {
      break // sin red: se corta y se reintenta en la proxima
    } else {
      // 422/402/500: conflicto real (stock, cupon, etc.). Se marca con el motivo
      // para resolverlo a mano; NO se elimina (no se pierde la venta).
      setConflict(item.id, res.message || t('pos.lbl_unexpected_error'))
    }
  }

  refreshQueue()
  syncing.value = false

  // El stock cambio con lo sincronizado: se relee el catalogo.
  if (store.branchId && isOnline.value) await loadCatalog()
}

const onAuthError = (httpStatus) => {
  authError.value = httpStatus
}

/** Centraliza 401/403: sin sesion/permiso el POS entero no aplica. */
const handleAuth = (res) => {
  if (res.httpStatus === 401 || res.httpStatus === 403) {
    authError.value = res.httpStatus
    return true
  }
  return false
}

const loadBranches = async () => {
  const res = await posRequest(POS_BRANCH_LIST())

  if (!res.ok) {
    if (handleAuth(res)) return
    globalError.value = res.message || t('pos.lbl_unexpected_error')
    return
  }

  const branches = Array.isArray(res.data) ? res.data : []
  store.setBranches(branches)

  // Una sola sucursal -> se selecciona sola y se carga su catalogo.
  if (branches.length === 1) {
    store.setBranch(branches[0].id)
    await loadCatalog()
  }
}

const loadCatalog = async () => {
  if (!store.branchId) return

  store.loadingCatalog = true
  globalError.value = ''

  const res = await posRequest(POS_CATALOG({ branch_id: store.branchId }))

  store.loadingCatalog = false

  if (!res.ok) {
    if (handleAuth(res)) return

    // Sin red (httpStatus 0) -> se intenta pintar el catalogo cacheado (offline).
    if (res.httpStatus === 0) {
      const cached = loadCachedCatalog(store.branchId)
      if (cached) {
        store.setCatalog(cached.catalog)
        offlineCatalog.value = true
        return
      }
    }

    globalError.value = res.message || t('pos.lbl_unexpected_error')
    return
  }

  store.setCatalog(res.data)
  offlineCatalog.value = false
  // Copia offline para la proxima vez que no haya red.
  saveCatalog(store.branchId, res.data)
}

const changeBranch = async (branchId) => {
  // Cambiar de sucursal invalida el carrito: los precios, el stock y los
  // empleados son de OTRA sucursal (el backend rechazaria las lineas).
  // `resetSale()` conserva a proposito la sucursal, asi que se fija despues.
  store.resetSale()
  store.setBranch(branchId)

  if (store.branchId) await loadCatalog()
  else store.setCatalog(null)
}

/**
 * COBRAR. El front no decide nada de dinero: manda IDs/cantidades y pinta la
 * respuesta. Si el backend devuelve error (402 cobro no capturado, 422 negocio,
 * 500), NO se registra venta ni se muestra "pagado": solo su `message`.
 */
const OFFLINE_METHODS = ['cash', 'transfer', 'cheque']

const charge = async () => {
  store.submitting = true
  store.clearError()

  const res = await posRequest(POS_SALE(), store.salePayload())

  store.submitting = false

  if (!res.ok) {
    if (handleAuth(res)) return

    // Sin red (httpStatus 0) + medio de pago OFFLINE -> se ENCOLA la venta (Fase 2).
    // El efectivo ya se cobro; la venta se registra y factura al reconectar.
    if (res.httpStatus === 0 && OFFLINE_METHODS.includes(store.effectiveMethod)) {
      const total = store.estimatedTotal
      const key = enqueueSale(store.salePayload())
      refreshQueue()
      store.setSale({ offline_queued: true, offline_key: key, total })
      return
    }

    store.setError(res.message || t('pos.lbl_unexpected_error'), flattenErrors(res.errors))
    return
  }

  // Snapshot del carrito para el recibo (el carrito se resetea al salir).
  res.data._receipt = {
    customer_name: store.customer?.name || '',
    lines_services: store.services.map((s) => ({ name: s.name, price: s.price })),
    lines_products: store.products.map((p) => ({ name: p.name, qty: p.qty, unit_price: p.unit_price }))
  }

  store.setSale(res.data)
}

/**
 * «Enviar a caja»: guarda la venta como orden abierta en el servidor (sin cobro).
 * Aparece a la encargada en «Llamar orden». El carrito se limpia para el siguiente cliente.
 */
const sendToCashier = async () => {
  store.submitting = true
  store.clearError()
  const res = await posRequest(POS_SEND_ORDER(), store.salePayload())
  store.submitting = false

  if (!res.ok) {
    if (handleAuth(res)) return
    store.setError(res.message || t('pos.lbl_unexpected_error'), flattenErrors(res.errors))
    return
  }

  store.resetSale()
  avisoEnvio.value = t('pos.lbl_sent_to_cashier')
  setTimeout(() => { avisoEnvio.value = '' }, 6000)
}
const avisoEnvio = ref('')

const newSale = async () => {
  // `resetSale()` conserva la sucursal (siguiente cliente en el mismo mostrador).
  store.resetSale()
  // El stock cambio con la venta anterior: se relee el catalogo.
  await loadCatalog()
}

/**
 * Regreso del widget de Datafast: la pasarela redirige al POS con `resourcePath`
 * en la URL. Se restaura el carrito guardado, se fija esa referencia y se limpia
 * la URL; el cajero confirma la venta y `pos/sale` verifica el cobro con Datafast.
 */
const handleDatafastReturn = async () => {
  const params = new URLSearchParams(window.location.search)
  const resourcePath = params.get('resourcePath')
  if (!resourcePath) return

  const branchId = store.restoreDraft()
  store.setDatafastReference(resourcePath)

  // Se limpia la URL para no re-disparar en un refresh.
  window.history.replaceState({}, '', `${window.location.pathname}?mode=pos`)

  if (branchId) {
    store.setBranch(branchId)
    await loadCatalog()
  }
}

onMounted(async () => {
  window.addEventListener('online', setOnline)
  window.addEventListener('offline', setOffline)
  await loadBranches()
  await handleDatafastReturn()
  // Si quedaron ventas offline de una sesion anterior, se sincronizan.
  if (queueCount.value > 0 && isOnline.value) await syncQueue()
})

onUnmounted(() => {
  window.removeEventListener('online', setOnline)
  window.removeEventListener('offline', setOffline)
})
</script>
